<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.addProduct") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #e1423d">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs" novalidate>
        <div class="row">
          <!-- Start:: Main Image Upload Input -->
          <base-image-upload-input
            col="12"
            identifier="main_image"
            :placeholder="$t('PLACEHOLDERS.productImg')"
            @selectImage="selectMainImage"
            required
          />
          <!-- End:: Main Image Upload Input -->

          <!-- Start:: Additional Images Upload Input -->
          <div class="col-12 my-5">
            <base-multi-image-upload-input
              @onFileSelect="handleAdditionalImages"
              @onFileRemove="removeAdditionalImage"
              :urls="additionalImagesUrls"
            >
              {{ $t("PLACEHOLDERS.additionalImages") || "Additional Images" }}
            </base-multi-image-upload-input>
          </div>
          <!-- End:: Additional Images Upload Input -->
          <!-- Start:: Name Input (Arabic) -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.codename')"
            v-model.trim="data.name"
            :maxlength="100"
            required
          />
          <!-- End:: Name Input (Arabic) -->

          <!-- Start:: Name Input (English) -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.codenameEn')"
            v-model.trim="data.nameEn"
            :maxlength="100"
            required
          />
          <!-- End:: Name Input (English) -->

          <!-- Start:: Description Input (Arabic) -->
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.descProd')"
            v-model.trim="data.descProd"
            :maxlength="500"
          />
          <!-- End:: Description Input (Arabic) -->

          <!-- Start:: Description Input (English) -->
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.descProdEn')"
            v-model.trim="data.descProdEn"
            :maxlength="500"
          />
          <!-- End:: Description Input (English) -->

          <!-- Start:: Category Select Input -->
          <base-select-input
            col="6"
            :optionsList="categoriesList"
            :placeholder="$t('PLACEHOLDERS.category') || 'Product Category'"
            v-model="data.category_id"
            required
          />
          <!-- End:: Category Select Input -->

          <!-- Start:: Weight or Size Input -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.weightOrSize') || 'Weight or Size'"
            v-model.number="data.weight_or_size"
            step="0.01"
            required
          />
          <!-- End:: Weight or Size Input -->

          <!-- Start:: Weight Unit Select Input -->
          <base-select-input
            col="6"
            :optionsList="measurementUnitOptions"
            :placeholder="$t('PLACEHOLDERS.weightUnit') || 'Weight Unit'"
            v-model="data.weight_unit"
            required
          />
          <!-- End:: Weight Unit Select Input -->

          <base-select-input
            col="6"
            :optionsList="frozenStatusList"
            :placeholder="$t('PLACEHOLDERS.frozen_status') || 'Freeze Status'"
            v-model="data.frozen_status"
            required
            hide-details
          />

          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.productCode') || 'Product Code'"
            v-model.trim="data.product_code"
          />

          <!-- Start:: Packaging & Inventory (General) -->
          <base-select-input
            col="6"
            :optionsList="packagingUnitOptions"
            :placeholder="$t('PLACEHOLDERS.packagingUnit') || 'Packaging Unit'"
            v-model="data.packaging_unit"
            required
          />

          <base-select-input
            col="6"
            :optionsList="itemUnitOptions"
            :placeholder="$t('PLACEHOLDERS.itemUnit') || 'Item Unit'"
            v-model="data.item_unit"
            required
          />

          <base-input
            col="6"
            type="number"
            :placeholder="
              $t('PLACEHOLDERS.availableUnits') || 'Available Units'
            "
            v-model.number="data.available_units"
            step="0.01"
            min="0"
            required
          />

          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.itemsPerUnit') || 'Items Per Unit'"
            v-model.number="data.items_per_unit"
            step="1"
            min="1"
            required
          />

          <base-input
            col="6"
            type="number"
            :placeholder="
              $t('PLACEHOLDERS.lowStockThreshold') ||
              'Low Stock Threshold (Optional)'
            "
            v-model.number="data.low_stock_threshold"
            step="1"
            min="0"
          />
          <!-- End:: Packaging & Inventory (General) -->

          <!-- Start:: Prices Section -->
          <div class="col-12">
            <div class="prices-section-header mb-4">
              <h5 class="mb-0" style="color: #e1423d; font-weight: 600">
                <i class="fas fa-dollar-sign mr-2"></i>
                {{
                  $t("PLACEHOLDERS.pricesByQuantity") || "Prices by Quantity"
                }}
              </h5>
            </div>

            <div class="prices-list">
              <div
                v-for="(price, index) in data.prices"
                :key="index"
                class="price-card mb-4"
              >
                <div class="price-card-header">
                  <div class="price-card-title">
                    <i class="fas fa-tag mx-2" style="color: #e1423d"></i>
                    <span style="font-weight: 600"
                      >{{ $t("PLACEHOLDERS.priceTier") || "Price Tier" }} #{{
                        index + 1
                      }}</span
                    >
                  </div>
                  <v-btn
                    v-if="data.prices.length > 1 && index !== 0"
                    @click="removePrice(index)"
                    color="error"
                    icon
                    small
                    class="remove-price-btn"
                  >
                    <i class="fas fa-trash m-auto"></i>
                  </v-btn>
                </div>

                <div class="price-card-body">
                  <div class="row">
                    <!-- Quantity -->
                    <div class="col-md-6 col-lg-4">
                      <base-input
                        col="12"
                        type="number"
                        :placeholder="$t('PLACEHOLDERS.quantity') || 'Quantity'"
                        v-model.number="price.quantity"
                        step="0.01"
                        required
                        :disabled="index === 0"
                      />
                    </div>

                    <!-- Price -->
                    <div class="col-md-6 col-lg-4">
                      <base-input
                        col="12"
                        type="number"
                        :placeholder="$t('PLACEHOLDERS.riyal_price') || 'Price'"
                        v-model.number="price.price"
                        step="0.01"
                        required
                      />
                    </div>

                    <!-- Minimum Quantity -->
                    <div class="col-md-6 col-lg-4">
                      <base-input
                        col="12"
                        type="number"
                        :placeholder="
                          $t('PLACEHOLDERS.minimumQuantity') ||
                          'Minimum Quantity'
                        "
                        v-model.number="price.minimum_quantity"
                        step="0.01"
                        min="1"
                        required
                      />
                    </div>

                    <!-- Price, Minimum only for price tier -->
                    <!-- Inventory and packaging moved to General section -->
                  </div>
                </div>
              </div>
            </div>

            <v-btn @click="addPrice" outlined class="add-price-btn">
              <i class="fas fa-plus mr-2"></i>
              {{ $t("BUTTONS.addPrice") || "Add Price" }}
            </v-btn>
          </div>
          <!-- End:: Prices Section -->

          <!-- Start:: Product Status Switch Input -->
          <div class="input_wrapper switch_wrapper my-5 col-6">
            <label
              class="form-label"
              style="margin: 0 13px !important; color: #e1423d"
            >
              {{ $t("PLACEHOLDERS.productStatus") || "Product Status" }}:
              <span class="text-danger">*</span>
            </label>
            <v-switch
              color="green"
              :label="
                data.is_active
                  ? $t('PLACEHOLDERS.active')
                  : $t('PLACEHOLDERS.notActive')
              "
              v-model="data.is_active"
              hide-details
            ></v-switch>
          </div>
          <!-- End:: Product Status Switch Input -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import BaseImageUploadInput from "../../../components/formInputs/BaseImageUploadInput.vue";
import BaseMultiImageUploadInput from "../../../components/formInputs/BaseMultiImageUploadInput.vue";

export default {
  name: "CreateProduct",

  components: {
    BaseImageUploadInput,
    BaseMultiImageUploadInput,
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        name: null,
        nameEn: null,
        descProd: null,
        descProdEn: null,
        main_image: {
          path: null,
          file: null,
        },
        category_id: null,
        frozen_status: null,
        product_code: null,
        weight_or_size: null,
        weight_unit: null,
        // Packaging & Inventory (product-level)
        packaging_unit: null,
        item_unit: null,
        available_units: null,
        items_per_unit: 1,
        low_stock_threshold: null,

        prices: [
          {
            quantity: 1,
            price: null,
            minimum_quantity: 1,
          },
        ],
        is_active: true,
      },
      // End:: Data Collection To Send

      categoriesList: [],
      additionalImages: [],
      additionalImagesUrls: [],
    };
  },

  computed: {
    packagingUnitOptions() {
      const isArabic = this.$i18n?.locale === "ar";
      return [
        { id: 1, value: "pack_bag", name: isArabic ? "كيس تعبئة" : "Pack Bag" },
        { id: 2, value: "carton", name: isArabic ? "كرتونة" : "Carton" },
        { id: 3, value: "gallon", name: isArabic ? "جالون" : "Gallon" },
        { id: 4, value: "bucket", name: isArabic ? "سطل" : "Bucket" },
        { id: 5, value: "tank", name: isArabic ? "تنك" : "Tank" },
        { id: 6, value: "packet", name: isArabic ? "باكيت" : "Packet" },
        { id: 7, value: "bundle", name: isArabic ? "ربطة" : "Bundle" },
        { id: 8, value: "tray", name: isArabic ? "طبق" : "Tray" },
      ];
    },

    itemUnitOptions() {
      const isArabic = this.$i18n?.locale === "ar";
      return [
        { id: 1, value: "bag", name: isArabic ? "كيس" : "Bag" },
        { id: 2, value: "kg", name: isArabic ? "كيلوجرام" : "Kilogram" },
        { id: 3, value: "liter", name: isArabic ? "لتر" : "Liter" },
        { id: 4, value: "piece", name: isArabic ? "قطعة" : "Piece" },
        { id: 5, value: "plate", name: isArabic ? "صحن" : "Plate" },
        { id: 6, value: "drzn", name: isArabic ? "دزينة" : "Dozen" },
      ];
    },

    measurementUnitOptions() {
      const isArabic = this.$i18n?.locale === "ar";
      return [
        {
          id: 1,
          name_ar: "كيس",
          name_en: "Bag",
          name: isArabic ? "كيس" : "Bag",
          value: "Bag",
        },
        {
          id: 2,
          name_ar: "بلوك",
          name_en: "Block",
          name: isArabic ? "بلوك" : "Block",
          value: "Block",
        },
        {
          id: 3,
          name_ar: "كرتون",
          name_en: "Box",
          name: isArabic ? "كرتون" : "Box",
          value: "Box",
        },
        {
          id: 4,
          name_ar: "جالون",
          name_en: "Gallon",
          name: isArabic ? "جالون" : "Gallon",
          value: "Gallon",
        },
        {
          id: 5,
          name_ar: "كيلو",
          name_en: "kg",
          name: isArabic ? "كيلو" : "kg",
          value: "kg",
        },
        {
          id: 6,
          name_ar: "لتر",
          name_en: "Ltr",
          name: isArabic ? "لتر" : "Ltr",
          value: "Ltr",
        },
        {
          id: 7,
          name_ar: "سطل",
          name_en: "Pail",
          name: isArabic ? "سطل" : "Pail",
          value: "Pail",
        },
        {
          id: 8,
          name_ar: "حبة",
          name_en: "Pc",
          name: isArabic ? "حبة" : "Pc",
          value: "Pc",
        },
        {
          id: 9,
          name_ar: "باكيت",
          name_en: "PKT",
          name: isArabic ? "باكيت" : "PKT",
          value: "PKT",
        },
        {
          id: 10,
          name_ar: "تنك",
          name_en: "Tin",
          name: isArabic ? "تنك" : "Tin",
          value: "Tin",
        },
        {
          id: 11,
          name_ar: "ربطة",
          name_en: "punch",
          name: isArabic ? "ربطة" : "punch",
          value: "punch",
        },
        {
          id: 12,
          name_ar: "طبق",
          name_en: "Tray",
          name: isArabic ? "طبق" : "Tray",
          value: "Tray",
        },
        {
          id: 13,
          name_ar: "صحن",
          name_en: "Plate",
          name: isArabic ? "صحن" : "Plate",
          value: "Plate",
        },
      ];
    },
    frozenStatusList() {
      return [
        { id: true, name: this.$t("PLACEHOLDERS.frozen"), value: "frozen" },
        { id: false, name: this.$t("PLACEHOLDERS.chilled"), value: "chilled" },
      ];
    },
  },

  methods: {
    // Start:: Image Handlers
    selectMainImage(selectedImage) {
      if (selectedImage.file) {
        // Validate file size (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (selectedImage.file.size > maxSize) {
          this.$message.error(
            this.$t("VALIDATION.fileSizeExceeded") ||
              "File size must not exceed 10MB"
          );
          return;
        }
        // Validate file type (images only)
        if (!selectedImage.file.type.startsWith("image/")) {
          this.$message.error(
            this.$t("VALIDATION.selectedFileMustBeImage") ||
              "Only image files are allowed"
          );
          return;
        }
      }
      this.data.main_image = selectedImage;
    },

    handleAdditionalImages(files) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      const validFiles = [];

      files.forEach((file) => {
        if (file.size > maxSize) {
          this.$message.error(
            `${file.name}: ${
              this.$t("VALIDATION.fileSizeExceeded") ||
              "File size must not exceed 10MB"
            }`
          );
          return;
        }
        if (!file.type.startsWith("image/")) {
          this.$message.error(
            `${file.name}: ${
              this.$t("VALIDATION.selectedFileMustBeImage") ||
              "Only image files are allowed"
            }`
          );
          return;
        }
        validFiles.push(file);
      });

      this.additionalImages = validFiles;
    },

    removeAdditionalImage(index) {
      this.additionalImagesUrls.splice(index, 1);
    },
    // End:: Image Handlers

    // Start:: Price Management
    addPrice() {
      this.data.prices.push({
        quantity: null,
        price: null,
        minimum_quantity: 1,
      });
    },

    removePrice(index) {
      if (this.data.prices.length > 1) {
        this.data.prices.splice(index, 1);
      }
    },
    // End:: Price Management

    // Start:: Get Categories
    async getCategories() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "product-categories?paginate=false&filter[is_active]=true",
          params: {
            limit: 0,
            page: 0,
            isActive: 1,
          },
        });
        this.categoriesList = (res.data.data?.data || res.data.data || []).map(
          (category) => ({
            id: category.id,
            name: category.name,
          })
        );
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Categories

    validateFormInputs() {
      this.isWaitingRequest = true;
      this.submitForm();
    },

    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = new FormData();
      // Start:: Append Request Data
      if (this.data.name) {
        REQUEST_DATA.append("name[ar]", this.data.name);
      }
      if (this.data.nameEn) {
        REQUEST_DATA.append("name[en]", this.data.nameEn);
      }
      if (this.data.descProd) {
        REQUEST_DATA.append("description[ar]", this.data.descProd);
      }
      if (this.data.descProdEn) {
        REQUEST_DATA.append("description[en]", this.data.descProdEn);
      }
      if (this.data.main_image.file) {
        REQUEST_DATA.append("main_image", this.data.main_image.file);
      }
      if (this.additionalImages.length > 0) {
        this.additionalImages.forEach((file) => {
          REQUEST_DATA.append("images[]", file);
        });
      }
      if (this.data.category_id) {
        REQUEST_DATA.append("category_id", this.data.category_id?.id);
      }
      REQUEST_DATA.append(
        "frozen_status",
        this.data.frozen_status?.value || ""
      );
      if (this.data.product_code) {
        REQUEST_DATA.append("product_code", this.data.product_code);
      }
      if (this.data.weight_or_size) {
        REQUEST_DATA.append("weight_or_size", this.data.weight_or_size);
      }
      if (this.data.weight_unit) {
        REQUEST_DATA.append(
          "weight_unit",
          this.data.weight_unit?.name || this.data.weight_unit
        );
      }

      // Append product-level packaging & inventory fields
      if (this.data.packaging_unit) {
        REQUEST_DATA.append(
          "packaging_unit",
          this.data.packaging_unit?.value || this.data.packaging_unit
        );
      }
      if (this.data.item_unit) {
        REQUEST_DATA.append(
          "item_unit",
          this.data.item_unit?.value || this.data.item_unit
        );
      }
      if (
        this.data.available_units !== undefined &&
        this.data.available_units !== null
      ) {
        REQUEST_DATA.append("available_units", this.data.available_units);
      }
      if (
        this.data.items_per_unit !== undefined &&
        this.data.items_per_unit !== null
      ) {
        REQUEST_DATA.append("items_per_unit", this.data.items_per_unit);
      }
      if (
        this.data.low_stock_threshold !== undefined &&
        this.data.low_stock_threshold !== null
      ) {
        REQUEST_DATA.append(
          "low_stock_threshold",
          this.data.low_stock_threshold
        );
      }

      // Append prices (only price-related fields)
      this.data.prices.forEach((price, index) => {
        REQUEST_DATA.append(`prices[${index}][quantity]`, price.quantity);
        REQUEST_DATA.append(`prices[${index}][price]`, price.price);
        if (price.minimum_quantity !== undefined) {
          REQUEST_DATA.append(
            `prices[${index}][minimum_quantity]`,
            price.minimum_quantity
          );
        }
      });

      REQUEST_DATA.append("is_active", this.data.is_active ? 1 : 0);
      // End:: Append Request Data

      try {
        await this.$axios({
          method: "POST",
          url: `products`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        this.$router.push({ path: "/products/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(error.response.data.message);
      }
    },
    // End:: Submit Form
  },

  created() {
    // Start:: Fire Methods
    this.getCategories();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.prices-section-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #e1423d;
}

.price-card {
  /* background: #ffffff; */
  border: 1px solid #e1423d;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  overflow: hidden;
}

.price-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #e1423d;
}

.price-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #fff5f4 0%, #ffe8e6 100%);
  border-bottom: 1px solid #e1423d;
}

.price-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-card-body {
  padding: 1.25rem;
}

.remove-price-btn {
  border: none;
  background: #ffe8e6 !important;
  color: #e1423d !important;
}

.remove-price-btn:hover {
  background: #ffd4d0 !important;
}

.add-price-btn {
  border: 2px solid #e1423d !important;
  color: #e1423d !important;
  font-weight: 600;
}

.add-price-btn:hover {
  background: rgba(225, 66, 61, 0.1) !important;
}

.inventory-info-box {
  background: #f0f8ff;
  border-left: 4px solid #1976d2;
  border-radius: 4px;
  padding: 10px 12px;
  color: #555;
  font-size: 12px;
  line-height: 1.6;
}

.inventory-info-box strong {
  color: #d32f2f;
  font-weight: 600;
}
</style>
